import { ISolicitacaoChatMessage, SolicitacaoChatMessageTypeEnum } from '@/types/interfaces/solicitacao-chat/solicitacao-chat-message.interface'
import { HubConnection } from '@microsoft/signalr'
import * as signalR from '@microsoft/signalr'
import EventBus from '@/plugins/EventBus'
import { ISolicitacaoChatCheckOnline } from '@/types/interfaces/solicitacao-chat/solicitacao-chat-check-online.interface'
import { ISolicitacaoChat } from '../types/interfaces/solicitacao-chat/solicitacao-chat.interface'

const env: any = window

class ChatConnectionService {
  private connection?: HubConnection
  private messageListeners: MessageEventFn[] = []
  private checkOnlineListeners: CheckOnlineEventFn[] = []
  private chatDataListeners: ChatDataEventFn[] = []
  private solicitationId?: string
  private senderId?: string

  private canTryReconnectOnCloseConnection = false

  /**
   * Create a new connection
   * @param solicitationId
   * @param senderId
   * @returns the connection
   */
  async openConnection(solicitationId: string, senderId: string) {
    this.canTryReconnectOnCloseConnection = true
    this.solicitationId = solicitationId
    this.senderId = senderId

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${env.API_FINX_SIGNALR_CHAT}`, {
        accessTokenFactory: () => this.accessToken,
      })
      .configureLogging(signalR.LogLevel.Information)
      .build()

    // Create event listners
    this.createListners()

    try {
      await this.connection!.start()
    } catch (err) {
      EventBus.$emit('setSnackbar', { show: true, text: 'Ocorreu um erro ao iniciar a comunicação com o Chat', icon: 'warning', color: '#C62828' })
    }

    try {
      await this.connection.invoke('EntrarNoChat', solicitationId, senderId)
    } catch (err) {
      EventBus.$emit('setSnackbar', { show: true, text: 'Ocorreu um erro ao entrar no Chat', icon: 'warning', color: '#C62828' })
    }

    return this.connection
  }

  /**
   * Kill the current connection
   */
  async closeConnection() {
    this.canTryReconnectOnCloseConnection = false

    try {
      if (this.connection) {
        await this.connection!.invoke('SairDoChat', this.solicitationId, this.senderId)
        await this.connection!.stop()
      }
      // eslint-disable-next-line no-empty
    } catch (err) {
      // eslint-disable-next-line
      console.warn(err)
    }

    this.messageListeners = []
    this.checkOnlineListeners = []

    this.connection = undefined
    this.solicitationId = undefined
    this.senderId = undefined
  }

  /** Send a CheckOnline event.
   * After send response for listners created in listenCheckOnlineEvent  */
  async loadCheckOnline() {
    await this.connection!.invoke('ChecarEstadoSolicitacaoChat', this.solicitationId)
  }

  /** Set all users from chat offline */
  async closeChat() {
    await this.connection!.invoke('EncerrarChat')
  }

  listenMessageEvent(data: MessageEventFn) {
    this.messageListeners.push(data)
  }

  listenCheckOnlineEvent(data: CheckOnlineEventFn) {
    this.checkOnlineListeners.push(data)
  }

  listenChatDataEvent(data: ChatDataEventFn) {
    this.chatDataListeners.push(data)
  }

  private createListners() {
    this.connection!.onclose(() => {
      if (this.canTryReconnectOnCloseConnection) {
        this.openConnection(this.solicitationId!, this.senderId!)
      }
    })

    this.connection!.on('ReceberRespostaDaMensagem', (data: any) => {
      // eslint-disable-next-line
      console.log('ReceberRespostaDaMensagem', data)

      if (data.usuarioId !== this.senderId) {
        const message = data
        const messageTypeKey = message.tipoMensagem!.key as any as number

        // ATTENTION!!!
        // Remove this line when API fix the problem in key that need return as string
        // eslint-disable-next-line default-case
        switch (messageTypeKey) {
          case 0:
            message.tipoMensagem!.key = SolicitacaoChatMessageTypeEnum.inicioAgendamento
            break

          case 1:
            message.tipoMensagem!.key = SolicitacaoChatMessageTypeEnum.fimAgendamento
            break

          case 2:
            message.tipoMensagem!.key = SolicitacaoChatMessageTypeEnum.inicioAutorizacao
            break

          case 3:
            message.tipoMensagem!.key = SolicitacaoChatMessageTypeEnum.fimAutorizacao
            break

          case 4:
            message.tipoMensagem!.key = SolicitacaoChatMessageTypeEnum.texto
            break

          case 5:
            message.tipoMensagem!.key = SolicitacaoChatMessageTypeEnum.enviadoParaAutorizacao
            break

          case 6:
            message.tipoMensagem!.key = SolicitacaoChatMessageTypeEnum.enviadoParaAgendamento
            break

          case 7:
            message.tipoMensagem!.key = SolicitacaoChatMessageTypeEnum.anexo
            break
        }

        // eslint-disable-next-line no-restricted-syntax
        for (const listner of this.messageListeners) {
          listner(message)
        }
      }
    })

    this.connection!.on('ReceberEstadoSolicitacaoChat', (data?: ISolicitacaoChatCheckOnline) => {
      // eslint-disable-next-line
      console.log('ReceberEstadoSolicitacaoChat', data)

      // eslint-disable-next-line no-restricted-syntax
      for (const listner of this.checkOnlineListeners) {
        listner(data)
      }
    })

    this.connection!.on('ReceberInformacoesSolicitacaoChat', (data?: Partial<ISolicitacaoChat>) => {
      // eslint-disable-next-line
      console.log('ReceberInformacoesSolicitacaoChat', data)

      // eslint-disable-next-line no-restricted-syntax
      for (const listner of this.chatDataListeners) {
        listner(data)
      }
    })
  }

  private get accessToken() {
    const localStorageData: string = localStorage.getItem(`oidc.user:${env.API_FINX_AUTH}:portal-client-2`)!
    const user: any = JSON.parse(localStorageData)
    return user.access_token
  }
}

type MessageEventFn = (data: ISolicitacaoChatMessage) => void
type CheckOnlineEventFn = (data?: ISolicitacaoChatCheckOnline) => void
type ChatDataEventFn = (data?: Partial<ISolicitacaoChat>) => void

// eslint-disable-next-line import/prefer-default-export
export const chatConnectionService = new ChatConnectionService()
